import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { BRLFormat } from "../../utils/currency";
import { getOrderById } from "../../service/order";
import { createPayment, processCardPayment } from "../../service/payment";
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';

initMercadoPago(process.env.REACT_APP_MP_PUBLIC_KEY || 'APP_USR-615f0e3f-eb30-4120-acf1-421bfddcfc0d', { locale: 'pt-BR' });

function PaymentScreen({ paymentData, setGeneratedNumbers, setPaymentData }) {
  const [timeLeft, setTimeLeft] = useState(300);
  const [timerActive, setTimerActive] = useState(false); // Timer só roda no PIX
  const { orderId } = useParams();
  const [isPooling, setIsPooling] = useState(false); // Só começa polling após escolher método
  const navigate = useNavigate();

  // null = nenhum método selecionado ainda, 'pix' ou 'card'
  const [activeTab, setActiveTab] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false); // Controla se o brick do cartão já foi inicializado

  const { selectedGame, qrcode, email, selectedProduct } = paymentData || {};
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Redirect if no payment data
    if (!paymentData) {
      navigate("/");
    }
  }, [paymentData, navigate]);

  // Gera o PIX somente quando o usuário clicar em "Pagar com PIX"
  const handleSelectPix = useCallback(() => {
    setActiveTab('pix');
    setIsPooling(true); // Começa polling ao escolher método
    setTimerActive(true); // Inicia timer apenas para PIX

    // Só cria o pagamento PIX se ainda não tiver qrcode
    if (!qrcode && paymentData && !pixLoading) {
      setPixLoading(true);
      createPayment(orderId, email, selectedGame?.id, selectedProduct)
        .then((result) => {
          setPaymentData({
            ...paymentData,
            qrcode: result.point_of_interaction.transaction_data.qr_code,
          });
          setPixLoading(false);
        })
        .catch((err) => {
          setPixLoading(false);
          console.log(`Failed to create pix payment - ${err.message}`);
        });
    }
  }, [qrcode, paymentData, pixLoading, orderId, email, selectedGame, selectedProduct, setPaymentData]);

  const handleSelectCard = useCallback(() => {
    setActiveTab('card');
    setCardReady(true); // Marca que o brick deve ser renderizado
    setTimerActive(false); // Timer não roda no cartão
    // Não precisa de polling para cartão - a resposta é síncrona
  }, []);

  // Polling para verificar se o pagamento foi confirmado
  useEffect(() => {
    let interval;
    if (isPooling) {
      interval = setInterval(() => {
        getOrderById(orderId).then((result) => {
          if (result?.games?.length) {
            setIsPooling(false);
            setGeneratedNumbers(
              result.games.map((item) => {
                return item.games;
              })
            );
            navigate(`/resultado/${orderId}`);
          }
        });
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isPooling, navigate, orderId, setGeneratedNumbers]);

  // Timer de expiração - só roda quando PIX está ativo
  useEffect(() => {
    if (!timerActive) return;
    if (timeLeft === 0) {
      navigate(`/`);
    }
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, navigate, timerActive]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const copyToClipboard = () => {
    const input = document.getElementById("copyInput");
    if (input) {
      input.select();
      input.setSelectionRange(0, 99999);
      navigator.clipboard.writeText(input.value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const initialization = useMemo(() => ({
    amount: selectedGame?.price || 0,
    payer: {
      email: email,
    },
  }), [selectedGame?.price, email]);

  const onSubmitCard = useCallback(async (formData, additionalData) => {
    setCardLoading(true);
    try {
      const result = await processCardPayment(orderId, email, selectedGame?.id, selectedProduct, formData, additionalData);
      
      // Cartão de crédito retorna status imediato do MP
      // Se aprovado, o backend já processou - redireciona direto
      if (result?.status === 'approved' || result?.status === 'processed') {
        // Inicia polling para pegar os jogos gerados
        setIsPooling(true);
        setCardLoading(false);
      } else if (result?.status === 'rejected') {
        setCardLoading(false);
        alert('Pagamento recusado. Verifique os dados do cartão e tente novamente.');
      } else {
        // Status pendente ou outro - inicia polling por segurança
        setIsPooling(true);
        setCardLoading(false);
      }
    } catch (error) {
      setCardLoading(false);
      console.error(error);
      alert('Erro ao processar o pagamento com cartão.');
    }
  }, [orderId, email, selectedGame?.id, selectedProduct]);

  const onErrorCard = useCallback(async (error) => {
    // Erros de CORS do tracking do MP são inofensivos, ignorar silenciosamente
    console.warn('MP CardPayment error (pode ser ignorado se for CORS de tracking):', error);
  }, []);

  const onReadyCard = useCallback(async () => {}, []);

  if (!paymentData) return null;

  return (
    <div className="container mt-5 payment-screen">
      <div className="product-details mt-4">
        <ul className="list-unstyled">
          <li className="details-item">
            <span className="detail-text">Total de jogos escolhidos:</span>
            <span className="detail-value">{selectedGame?.numOfGames}</span>
          </li>
          <li className="details-item">
            <span className="detail-text">Valor:</span>
            <span className="detail-value">
              {BRLFormat(selectedGame?.price)}
            </span>
          </li>
        </ul>
      </div>

      {/* Seleção de método de pagamento */}
      <div className="d-flex justify-content-center mt-4 mb-4">
        <button 
          className={`btn ${activeTab === 'pix' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
          onClick={handleSelectPix}
          style={{ borderRadius: '25px', padding: '10px 20px' }}
        >
          Pagar com PIX
        </button>
        <button 
          className={`btn ${activeTab === 'card' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={handleSelectCard}
          style={{ borderRadius: '25px', padding: '10px 20px' }}
        >
          Pagar com Cartão
        </button>
      </div>

      {/* Mensagem quando nenhum método foi selecionado */}
      {activeTab === null && (
        <div className="text-center my-5" style={{ color: '#888' }}>
          <p style={{ fontSize: '18px' }}>Escolha acima como deseja pagar</p>
        </div>
      )}

      {/* Aba PIX - só mostra quando o usuário clicar em PIX */}
      {activeTab === 'pix' && (
        <>
          {pixLoading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Gerando PIX...</span>
              </div>
              <p className="mt-2">Gerando código PIX...</p>
            </div>
          ) : (
            <>
              <div className="qrcode-box text-center">
                {qrcode ? <QRCode value={qrcode} /> : null}
              </div>
        
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <input
                  type="text"
                  id="copyInput"
                  className="input-box"
                  placeholder="Aguardando código..."
                  style={{
                    width: '80%',
                    borderRadius: '50px',
                    border: '2px solid #ffcc00',
                    padding: '10px 15px',
                    fontSize: '16px',
                    marginRight: '5px'
                  }}
                  value={qrcode || ""}
                  readOnly={true}
                />
                <br />
                <button
                  onClick={copyToClipboard}
                  style={{
                    borderRadius: "50px",
                    padding: "15px 30px",
                    fontSize: "18px",
                    backgroundColor: "#ffcc00",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    marginTop: "10px"
                  }}
                  disabled={!qrcode}
                >
                  Pix Copia e Cola
                </button>
                {copied && (
                  <div style={{ marginTop: "10px", color: "#ffcc00", fontWeight: "bold" }}>
                    Copiado!
                  </div>
                )}
              </div>
              
              <div className="step-by-step mt-4">
                <ol className="step-list">
                  <li>Escolha a opção pagar com Pix no aplicativo de seu banco.</li>
                  <li>Aponte a câmera para o QRCode exibido na tela ou cole o código PIX copiado.</li>
                  <li>Confirme o valor do pagamento e finalize a transação.</li>
                </ol>
              </div>
            </>
          )}
        </>
      )}

      {/* 
        Aba Cartão - Uma vez inicializado, mantém o brick montado usando display CSS
        para evitar re-inicializações que causam erros CORS do MP.
      */}
      <div 
        className="card-payment-container mt-4"
        style={{ display: activeTab === 'card' ? 'block' : 'none' }}
      >
        {cardReady && (
          <CardPayment
            initialization={initialization}
            onSubmit={onSubmitCard}
            onReady={onReadyCard}
            onError={onErrorCard}
          />
        )}
        {cardLoading && (
          <div className="text-center mt-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Processando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Timer só aparece no PIX */}
      {activeTab === 'pix' && (
        <div className="timer mt-4 text-center">
          <p>Tempo restante para o pagamento: {formatTime(timeLeft)}</p>
        </div>
      )}
    </div>
  );
}

export default PaymentScreen;
